import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import {
  Badge,
  Card,
  EmptyState,
  PageBody,
  SectionLabel,
  Spinner,
  backLinkVariants,
} from '@wundo/ui';
import { ApiError } from '@wundo/client/api.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { runStatusTone } from '@/components/admin/recommendation-runs/runStatus.ts';
import { ScoreBreakdown } from '@/components/admin/recommendations/ScoreBreakdown.tsx';
import {
  fetchRecommendationRunDetail,
  type RecommendationEvaluatedSkill,
} from '@/lib/skillRecommendations.ts';

export const Route = createFileRoute(
  '/_app/admin/recommendation-runs/$runId/users/$userId',
)({
  staticData: { orgScope: 'none' },
  component: RunHistoryDetailRoute,
});

function RunHistoryDetailRoute() {
  const { runId, userId } = Route.useParams();
  return <RunHistoryDetail runId={runId} userId={userId} />;
}

/**
 * One user's slice of one run: the outreach that was written, and the scoring
 * log it was chosen from.
 *
 * Two sections because they come from two tables and mean two different things
 * — `skill_recommendations` is what the user will be sent, and
 * `skill_recommendation_scores` is the audit trail that explains it.
 */
export function RunHistoryDetail({ runId, userId }: { runId: string; userId: string }) {
  const {
    data: detail,
    isPending,
    error,
  } = useQuery({
    queryKey: ['recommendationRun', runId, userId],
    queryFn: () => fetchRecommendationRunDetail(runId, userId),
  });

  if (error instanceof ApiError && error.status === 404) {
    return (
      <>
        <BackToRuns />
        <PageHeader title='Recommendation run detail' />
        <PageBody className='mt-3.5'>
          <Card>
            <EmptyState title='Run not found' />
          </Card>
        </PageBody>
      </>
    );
  }

  if (error) {
    throw error;
  }

  return (
    <>
      <BackToRuns />
      <PageHeader title='Recommendation run detail' />
      {/* `@container`: the split folds on the column it is in, not the window. */}
      <PageBody className='@container mt-3.5 flex flex-col gap-5'>
        {isPending && (
          <Card>
            <div className='flex items-center gap-2 py-3 text-sm font-semibold text-ink-muted'>
              <Spinner />
              Loading…
            </div>
          </Card>
        )}

        {detail && (
          /*
           *The winner is the summary and the scoring log is the evidence, so
           *they stand beside each other rather than one below the other — until
           *the page is too narrow for two columns of it.
           */
          <div className='grid grid-cols-3 items-start gap-5 @max-agenda:grid-cols-1'>
            <Card as='section' tone='quiet'>
              <div className='mb-1 flex items-baseline justify-between gap-3'>
                <SectionLabel as='h2'>Outreach</SectionLabel>
                <span className='font-mono text-xs text-ink-muted'>
                  from skill_recommendations
                </span>
              </div>
              <p className='mb-3 text-xs text-ink-muted'>
                The one candidate the engine picked for this user. Status tracks what
                happened to the outreach.
              </p>
              <dl className='grid grid-cols-2 gap-x-6 gap-y-2 text-sm max-mobile:grid-cols-1'>
                <div>
                  <dt className='text-xs text-ink-muted'>Skill</dt>
                  <dd className='font-semibold text-ink'>{detail.winner.skill_name}</dd>
                </div>
                <div>
                  <dt className='text-xs text-ink-muted'>User</dt>
                  <dd className='text-ink'>{detail.winner.user_name ?? '—'}</dd>
                </div>
                <div>
                  <dt className='text-xs text-ink-muted'>Status</dt>
                  <dd>
                    <Badge tone={runStatusTone(detail.winner.status)}>
                      {detail.winner.status}
                    </Badge>
                  </dd>
                </div>
                <div>
                  <dt className='text-xs text-ink-muted'>Final score</dt>
                  <dd className='font-mono text-ink'>
                    {detail.winner.final_score.toFixed(1)}
                  </dd>
                </div>
                <div className='col-span-2 max-mobile:col-span-1'>
                  <dt className='text-xs text-ink-muted'>Reason</dt>
                  <dd className='text-ink-secondary'>{detail.winner.reason}</dd>
                </div>
                {detail.winner.skipped_reason && (
                  <div className='col-span-2 max-mobile:col-span-1'>
                    <dt className='text-xs text-ink-muted'>Skipped reason</dt>
                    <dd className='text-ink-secondary'>{detail.winner.skipped_reason}</dd>
                  </div>
                )}
              </dl>
            </Card>

            <section className='col-span-2 @max-agenda:col-span-1'>
              <div className='mb-1 flex items-baseline justify-between gap-3'>
                <SectionLabel as='h2'>
                  Scoring log ({detail.evaluated.length})
                </SectionLabel>
                <span className='font-mono text-xs text-ink-muted'>
                  from skill_recommendation_scores
                </span>
              </div>
              <p className='mb-3 text-xs text-ink-muted'>
                Every skill the LLM scored for this user in this run. The winner became
                the outreach above; the rest are kept as an audit trail.
              </p>
              <ul className='flex flex-col gap-2'>
                {detail.evaluated.map((evaluated) => (
                  <EvaluatedCard key={evaluated.skill_id} evaluated={evaluated} />
                ))}
              </ul>
            </section>
          </div>
        )}
      </PageBody>
    </>
  );
}

function EvaluatedCard({ evaluated }: { evaluated: RecommendationEvaluatedSkill }) {
  return (
    <Card as='li' tone={evaluated.was_selected ? 'accent' : 'surface'}>
      <div className='flex items-start justify-between gap-2'>
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-2'>
            <h3 className='text-sm font-semibold text-ink'>{evaluated.skill_name}</h3>
            {evaluated.was_selected && <Badge tone='success'>selected</Badge>}
          </div>
          <p className='mt-1 text-sm text-ink-secondary'>{evaluated.reason}</p>
        </div>
        <span className='font-mono text-sm font-semibold text-ink'>
          {evaluated.final_score.toFixed(1)}
        </span>
      </div>
      <ScoreBreakdown
        llmScore={evaluated.llm_score}
        boost={evaluated.boost}
        freshnessFactor={evaluated.freshness_factor}
        finalScore={evaluated.final_score}
        daysSinceLastUsed={evaluated.days_since_last_used}
      />
    </Card>
  );
}

/* The way back, in the gap PageBody leaves above its first child. */
function BackToRuns() {
  return (
    <Link
      to='/admin/recommendation-runs'
      search={{ page: 1 }}
      className={backLinkVariants({ className: 'mb-2.5' })}
    >
      <ChevronLeft aria-hidden='true' />
      Recommendation runs
    </Link>
  );
}
